/**
 * The resolver's shared vocabulary: the {@link InputSource} seam every die is drawn
 * through, the {@link EffectsConfig} the effects engine rides in on, and the
 * {@link ResolveResult} union every entry point in this directory returns.
 */

import type {
  Cell,
  ApplyResolvedCommand,
  ResolvedStep,
  DamageType,
  EncounterState,
  ResourceDeltaStep,
} from "@lancer-vtt/domain";
import type { PromptAnswerKind, RequestAttack } from "@lancer-vtt/shared";
import type { DiceSource } from "../../dice";
import type { ForcedAttackRequest, MovePath, OverlayEntry, ResolutionLedger } from "../effects";
import type { DecisionSection } from "../effects/choices";
import type { EventLoop } from "../events/loop";

/**
 * The single seam through which the resolver acquires *all* inputs. **The resolver must
 * never call a `DiceSource` or `Math.random` directly — only this.** Every draw has to be
 * recordable, because every other participant (and replay) folds the recorded steps
 * rules-free; a draw that bypasses this seam is a draw replay cannot reproduce.
 */
export type InputSource = {
  /** The authority's dice. */
  readonly dice: DiceSource;
  /** The intent's event loop, when the caller holds one: every trigger fired from this input raises into it. */
  readonly loop?: EventLoop;
}

/** Wrap a {@link DiceSource} (and the intent's loop, when there is one) as an {@link InputSource}. */
export function diceInputSource(dice: DiceSource, loop?: EventLoop): InputSource {
  return loop !== undefined ? { dice, loop } : { dice };
}

/**
 * Effects-engine config for the resolver. Carries the entity overlay (id → effects) the
 * trigger bus + modifier folding read against. Optional everywhere — a resolve without it
 * skips the atom path entirely (no status math, no triggers, just the bare attack spine).
 * Defaults to the hand-modeled `SEED_OVERLAY` when supplied with no overlay of its own.
 */
export type EffectsConfig = {
  /** The entity overlay the buses gather handlers/modifiers from. Defaults to the seed overlay. */
  overlay?: Record<string, OverlayEntry>;
  /** The intent's event loop (`events/loop.ts`); the resolution's ledger carries it to every fire. */
  loop?: EventLoop;
  /**
   * **Interactive mode** (combat-resolution-ui, step 4): when `true`, an unanswered
   * `choose` halts resolution and the resolver returns `needs_decision` (the prompt
   * round-trip), instead of silently taking the first option. The server's live path
   * sets it so a player is prompted; replay / the CLI's pre-recorded `--choice` leave
   * it `false` (the deterministic default). Recorded answers in `intent.choices` are
   * honored either way — interactivity only governs the *unanswered* case.
   */
  interactive?: boolean;
  /**
   * **Can a SPATIAL answer get back here?** (`granted-movement-allowances` Phase 3 addendum.)
   * {@link interactive} says the caller is a live round-trip; this says whether the round-trip
   * has a wire slot for a direction/cell answer specifically. The client answers a direction
   * halt by re-submitting the ECHOED INTENT with the picked cell recorded on it, and only some
   * intent shapes carry a field for that; one that doesn't hangs the encounter on a prompt with
   * nowhere to answer into. `false` degrades those atoms to their faithful
   * non-interactive default (plus a warning) instead of raising an unanswerable prompt; absent
   * reads as answerable. Set by `refereeIntent` off the intent's own shape — see
   * `intentCarriesReactionToward` there, and {@link
   * import("../effects").EffectContext.directionAnswerable} for the full account.
   */
  directionAnswerable?: boolean;
  /**
   * **This resolution is a forced attack's** (`npc-features-fielded-roster` Phase 11.1), set only
   * by `referee/forcedAttacks.ts` from the queued request's switches. `reactions: false` opens no
   * reaction window (to-hit or damage); `consume: false` skips S8's Loading, Limited and Recharge
   * consumes; `single: true` resolves an area weapon as one single-target attack (Rebound's
   * reflection repeats only the attack against the Ronin). `secondary: true` is a weapon's own
   * secondary attack (the Annihilator's): no Heat (Self), no bonus damage, and the weapon's own
   * on-hit clauses do not fire again.
   */
  forced?: { reactions?: false; consume?: false; single?: true; autoHit?: true; secondary?: true };
}

/**
 * The interactivity flags every `fireTrigger` call threads onto the fired handler's
 * {@link import("../effects").EffectContext}. **Spread this as one unit at every fire site** —
 * hand-copying the individual flags is how the sites drift apart the next time one is added.
 */
export function interactiveFire(effects: EffectsConfig | undefined): { interactive: boolean; directionAnswerable?: boolean } {
  return {
    interactive: effects?.interactive === true,
    ...(effects?.directionAnswerable !== undefined ? { directionAnswerable: effects.directionAnswerable } : {}),
  };
}

/**
 * A fully-resolved outcome: the `apply_resolved` command (the recorded step list,
 * the thing that flows through the reducer/log/broadcast), its inverse step list
 * (grounded deltas reversed, for the caller's convenience), and the events. A1 only
 * ever produces this.
 */
export type Resolved = {
  kind: "resolved";
  /** The resolution command to apply through the reducer / append to the log. */
  command: ApplyResolvedCommand;
  /** The recorded steps (also inside `command.steps`; surfaced for convenience). */
  steps: ResolvedStep[];
  /**
   * **Advisory warnings** raised during resolution (Phase 3.2 / A4-min, Decision
   * 6) — e.g. "attacker is Stunned … resolved anyway (advisory)", or a note that a
   * spatial status rider was deferred (D10). The resolution **still proceeds**;
   * these are surfaced for a driver/CLI to display, never a rejection. Empty when
   * nothing was flagged. Transient: warnings are *not* recorded into the step list
   * / retained log — they describe the resolution, they don't mutate state.
   */
  warnings: string[];
  /**
   * **Every reactor whose reaction this resolution spent** (`reactive-defenses` Phase 1,
   * `decisions/0047`; generalized to an array by `reactive-defenses-content` P3). Set when one or
   * more reactions fired during this attack — a target-side reaction (Brace) and/or a
   * protector's Intercede, each a DIFFERENT combatant that can both spend a reaction on the SAME
   * hit: the room folds one sibling `spend_reaction { combatantId, name }` per entry into the
   * attacker's batch so undoing the attack refunds every reaction spent. `undefined`/empty for
   * every resolution that spent no reaction (the overwhelming majority) — the room then folds
   * nothing extra.
   *
   * **Across an area fan-out this is a plain concat, NOT deduped** (`area-attack-resource-cap-
   * consume` Phase 2 — {@link resolveAreaAttack} used to drop it entirely, which is how a
   * defender Braced against a Blast and kept their reaction). Each entry is a different reactor
   * paying for their own damage instance, unlike {@link resourceCapConsumeSteps}, which settlement
   * collapses per (carrier, key). Every field of this type carries a declared disposition
   * at that fan-out — see `resolvedFieldDisposition.pin.test.ts`, which fails if a new one doesn't.
   */
  reactionSpentBy?: { combatantId: string; name: string }[];
  /**
   * **What an activation's resolution amounted to** (`decisions/0162`), set only by
   * {@link import("./activate").resolveActivate}. Meaningful when the authored body emitted no steps:
   *   - `"gated"` — every clause's own `condition` was false; nothing ran. Free.
   *   - `"declined"` — the only outcome was an authored empty `choose` option. Free.
   *   - `"ran"` — anything else, including a body that ran and emitted nothing (a `for_each` over an
   *     empty area, a `roll` that missed with no `on_miss`). Charged: the referee runs the economy
   *     fold and broadcasts the zero-step resolution.
   * `refereeIntent` fences on it instead of on `steps.length === 0`, which cannot tell the three
   * apart. **Transient, like {@link warnings}**: it describes the resolution and never reaches the
   * `apply_resolved` command, the wire, or the retained log.
   */
  disposition?: "gated" | "declined" | "ran";
  /**
   * For a `"gated"` activation, the first gated clause's failed requirement in table language
   * ({@link import("../effects/predicatePhrase").unmetRequirement}) — what `refereeIntent`'s
   * rejection tells the player. Transient, like {@link disposition}.
   */
  unmetCondition?: string;
  /**
   * The area-weapon template's anchor cell (`combat-impact-vfx` Phase 4) — the Blast/Burst disk
   * center or the Line/Cone `toward` endpoint {@link areaTemplate} built — present ONLY when this
   * resolution went through {@link resolveAreaAttack}'s multi-target fan-out. A presentation-only
   * hint: the frontend uses it to fly ONE shared projectile/impact at this cell instead of one per
   * target (a large target count would otherwise blow the animation backpressure cap). `undefined`
   * for every single-target resolution — never persisted onto {@link ApplyResolvedCommand} itself,
   * since it drives no game rule.
   */
  areaOrigin?: Cell;
  /**
   * The attack-ordinal counter's **increment step** (`ll0-dsl-primitives-2` Phase D,
   * `decisions/0085` — the native attack-family `match.firstAttackThisTurn` fact's increment
   * half), set ONLY by
   * {@link resolve} (never by `resolveActivate`/`resolveStabilize`/`resolveOvercharge`/
   * `resolveFullTech` — `on_attack_declared`'s own scope is attack-shaped resolutions only,
   * mirroring `fireAttackDeclared`'s own choke point), whenever this resolution actually
   * committed (`sub.kind === "resolved"`). Deliberately NOT folded into {@link steps}/{@link
   * command}'s own step list — mirrors {@link reactionSpentBy}'s own "extra bookkeeping the room
   * folds as a sibling command" shape: the room applies this as its own `resource_delta` sibling
   * (`encounterRoom.ts`), the SAME way `reactionSpentBy` becomes a sibling `spend_reaction`. Kept
   * separate from `command.steps`, the room's bookkeeping beside the record. Moving it into the
   * record is a behaviour change to the many exact-step attack tests (`resolve.a4.test.ts` et al.),
   * re-blessed through the behaviour-change ledger under umbrella decision 0.3 when
   * `event-engine-costs` makes it a cost event. `undefined` for a resolution the effects engine never touched at all is
   * impossible to distinguish here (this field is always set when `resolve` reaches "resolved"),
   * but that's harmless: the room only ever applies it when it actually has a `resource_delta` to
   * fold, and an extra `attack_ordinal_this_turn` counter tick on a combatant nobody ever reads
   * `match.firstAttackThisTurn` against is inert.
   */
  ordinalIncrement?: ResourceDeltaStep;
  /**
   * `modifier_consume_resource_on_apply`'s cap spends (`ll0-dsl-primitives-2` Phase E,
   * `decisions/0085`): one `resource_delta +1` on `resourceCapKey(key)` per (carrier, key) that any
   * capped modifier or triggered clause used this declaration, in raise order (`settleEvents`,
   * `events/gameEvents.ts`; BC-024, BC-025). Kept beside {@link steps}/{@link command}, not in
   * them: the room folds it as its own sibling addition to `command.steps`, as it does
   * {@link ordinalIncrement}. `undefined` when nothing spent a cap.
   */
  resourceCapConsumeSteps?: ResourceDeltaStep[];
  /**
   * The promptId of the direction (`"chosen"`-mode) halt this resolution's mover/chooser atom
   * actually consumed, when one did (`granted-movement-allowances` Phase 1.2, D3's site D —
   * "have the engine report the id it answered"). Read straight off {@link
   * import("../effects/resolutionLedger").ResolutionLedger.answeredDirectionPromptId} at the end
   * of {@link resolve} — see that field's own doc for the full mechanism. This is the ONE
   * authoritative answer to "which decision did this `resolve()` pass just answer": a caller
   * that instead reconstructs `move_toward:${intent.target.id}` (the pre-1.2 shape) silently
   * assumes the mover is always the intent's target, which a declared-phase self-lunge
   * (`t_siege_specialist:2`, `t_hunter:1`) breaks — the mover there is the ATTACKER.
   * `undefined` when nothing in this resolution consumed a chosen-direction answer.
   */
  answeredDirectionPromptId?: string;
  /**
   * **Forced attacks this resolution queued** (`forced_attack`, `npc-features-fielded-roster`
   * Phase 13.2 — Aggression's Chain Axe swing, Avalanche Charge's). Not steps: nothing here has
   * rolled yet. `refereeIntent` resolves each against the post-resolution state
   * (`referee/forcedAttacks.ts`), appends the nested attack's steps and warnings to THIS
   * resolution, and strips the field; a caller that does not run the referee drops them.
   * `undefined` when none were queued.
   */
  forcedAttacks?: ForcedAttackRequest[];
  /**
   * **Every combatant this NON-ATTACK resolution damaged** (`damage-outside-the-reaction-window`,
   * `decisions/0175`) — the emitter's own report, which the referee hands to
   * `fireDamageTakenInto` so each id's `on:"damage"` clauses fire on the trigger bus after the
   * resolution commits. Set by the emitter, never derived from its steps: an immunity-nullified or
   * fully-absorbed hit emits no `hp_delta` and is still a hit (Personal Shield breaks on it).
   *
   * **Never set on the attack path.** Attack damage fires its notification clauses through
   * `reactionRegistry.ts`'s `compileAutomaticOutcome`, before mitigation; populating this there
   * would fire them twice. `undefined` when the resolution damaged nobody. Set by
   * `resolveBurnTick`'s and `resolveDangerousTerrainCheck`'s fail legs; the other non-attack
   * emitters return their own result types and carry the same `damaged` field
   * (`resolveDamageTrigger`'s site audit lists them).
   *
   * Each report carries the emitter's post-mitigation amount ({@link DamageReport.final}), which
   * the bus fire turns into the `dealt` fact a settled-damage clause (Aggression) matches on.
   */
  damaged?: DamageReport[];
  /**
   * **The walk this resolution's `move_self_toward_target` made** (`EffectContext.movePath`):
   * an activation's own move (Charge, Impale, Avalanche Charge), or the walk an attack's on-hit
   * clause forces on its target (Puppet System). Handed to the referee so the move hazards a
   * `request_move` runs — dangerous terrain, the mine proximity trigger, a Trampling mover's
   * Trample sweep, the move trigger family — fire on it too. Set by `resolveActivate` and the
   * attack resolver, on a move that landed. `undefined` otherwise.
   */
  movePath?: MovePath;
}

/**
 * One combatant a NON-ATTACK emitter damaged, and how much (`Resolved.damaged`). `final` is the
 * emitter's post-mitigation amount before Overshield absorbs any of it, so a hit the shield soaked
 * whole still reads as dealt, the same rule the attack path's settled-damage pass uses
 * (`damageApply.ts`). An immunity-nullified or 0-final hit reports `final: 0`: still a hit for an
 * "if you take any damage" clause, never `dealt`.
 */
export type DamageReport = {
  id: string;
  final: number;
  /** The damage's type, so a `match.dtype` clause can tell it apart (Regenerator's energy damage, `npc-gaps` 2.6). */
  dtype: DamageType;
}

/** One selectable option a {@link NeedsDecision} offers (the `choose` atom option projected). */
export type DecisionOption = {
  id: string;
  label: string;
  /** The map space this option names — set on every option of a `"cell"` decision (the id is its key). */
  cell?: Cell;
}

/**
 * **A resolution that needs a remote human choice mid-pass** (combat-resolution-ui,
 * step 4 — the seam finally wired). When the resolver runs interactively (the room's
 * {@link promptingChoices} source) and hits a `choose` with no recorded answer, it
 * returns this instead of steps; the authority ships the prompt to `who`, the client
 * records the answer into the intent's `choices`, and re-submits → the re-resolve
 * finds the recorded answer and completes. Iterate-pure: each round-trip records one
 * more choice and re-runs the whole resolution from the top (no held server state).
 */
export type NeedsDecision = {
  kind: "needs_decision";
  /** Which actor must answer. */
  who: string;
  /** The `choose` atom's stable prompt id — the key the answer records under in `intent.choices`. */
  promptId: string;
  /** A human-readable prompt for the decision. */
  prompt: string;
  /** The options to choose among. */
  options: DecisionOption[];
  /**
   * The decision's answer shape (`fire-context-map-click` Phase 1, mirrors {@link
   * import("../effects").NeedsDecisionSignal}'s own field) — `"options"` (the pre-existing
   * default, `options` populated) or `"direction"` (a spatial pick; `options` is `[]`, unused —
   * the client answers via the CLI's `--react-toward` or the browser's map-click mode, Phase 2).
   * Omitted reads as `"options"` (every pre-Phase-1 site). `"combatant"` is a pick among the
   * listed candidate combatants, answered like an option (`choose_from_area`); `"cell"` is a pick
   * among the listed map spaces, each option carrying its `cell`, answered like an option.
   */
  answerKind?: PromptAnswerKind;
  /**
   * For a **damage-interception reaction** (Brace): the damage the target is about to take from the
   * triggering attack — surfaced so the decider can weigh reacting *after* seeing the hit (the
   * window opens after damage is rolled — for a rider weapon, after EVERY instance's dice, per
   * `npc-weapon-rider-damage` Phase 3 decision 7). Display-only; absent for non-damage prompts
   * (variable damage-type, Lock On). The two finals are mitigated both ways at the prompt site.
   * `rolled`/`ifDeclined`/`ifReacted` are **summed across every damage instance** (primary + any
   * riders) — an attack-wide total, not just the primary's.
   */
  incomingDamage?: {
    /** The primary instance's damage type (a rider weapon's own types ride `breakdown` below). */
    type: DamageType;
    /** Pre-mitigation rolled total, summed across every instance (before armor and any reaction). */
    rolled: number;
    /** Final HP loss if the reaction is DECLINED (armor applied, no Resistance), summed. */
    ifDeclined: number;
    /** Final HP loss if the reaction is TAKEN (Brace's Resistance — halved after armor), summed. */
    ifReacted: number;
    /**
     * **Per-instance breakdown** (`npc-weapon-rider-damage` Phase 3, decision 7) — one entry per
     * damage instance in source order (primary first), present only when the weapon carries riders
     * (`weapon.riders`). The top-level `rolled`/`ifDeclined`/`ifReacted` above are these entries'
     * sums; this is additive display detail for a future per-instance prompt breakdown UI (Phase
     * 4). Absent for every single-instance weapon — the overwhelming common case, where the summed
     * top-level figures alone already describe the whole incoming hit.
     */
    breakdown?: { type: DamageType; rolled: number; ifDeclined: number; ifReacted: number }[];
  };
  /**
   * **The paintable "where can this magnitude pick land" set** (`granted-movement-allowances`
   * Phase 3.3) — display-only, mirrors {@link import("../effects").NeedsDecisionSignal}'s own
   * field of the same name (computed once, at the atom's throw site, where the authored
   * `dist`/`towardCell`/`as` are in scope — never re-derived here). Populated only for the
   * fixed-direction magnitude pick (Hunter's Lunge shape); absent for a `toward:"chosen"` halt
   * and every non-spatial `choose` — the browser falls back to a bare crosshair when it's absent.
   */
  reachableCells?: Cell[];
  /** A multi-section placement's progress — mirrors {@link import("../effects").NeedsDecisionSignal}'s own field. Display-only. */
  section?: DecisionSection;
  /** The decision's short name (`Brace`) — mirrors {@link import("../effects").NeedsDecisionSignal}'s own field. Display-only. */
  title?: string;
  /** The optional one-line cost summary — mirrors {@link import("../effects").NeedsDecisionSignal}'s own field. Display-only. */
  summary?: string;
}

/** The discriminated-union result of {@link resolve}. A1 only ever returns `Resolved`. */
export type ResolveResult = Resolved | NeedsDecision;

/**
 * **The stage guard** for `resolve/attack.ts`'s extracted `resolveSingleTarget` stages
 * (`resolve-single-target-decomposition` D2). Each exit-carrying stage returns
 * `StageOutput | NeedsDecision` — its own settled record, or the prompt that halts the whole
 * resolution — and its caller forwards the halt with one uniform line:
 *
 * ```ts
 * const stage = someStage({ … });
 * if (isNeedsDecision(stage)) return stage;
 * ```
 *
 * A plain `kind === "needs_decision"` property comparison cannot be written at those call sites:
 * a stage's own output record carries no `kind` field (nothing about a settled damage type or a
 * list of consume steps wants one), so the union is not discriminated and tsc rejects the
 * property read. This predicate is that check, written once — which also keeps the halt-forwarding
 * idiom identical at every stage seam instead of each site inventing its own `in` test.
 */
export function isNeedsDecision(v: object): v is NeedsDecision {
  return "kind" in v && (v as { kind: unknown }).kind === "needs_decision";
}

/** A typed rejection: the intent can't be resolved against this state (e.g. unknown combatant). */
export class ResolveError extends Error {}

/**
 * The **variable damage-type "choose the type"** prompt's stable recorded-answer key (the
 * A2-finish account lives on {@link import("./attackPreRoll").resolveVariableDamageType}, the
 * stage that builds this prompt) — keyed on the weapon's own id, since RAW's variable-type
 * text is one type choice per attack (a rider inherits the SAME resolved choice, never a second
 * prompt). Exported (mirroring {@link import("../tables").systemTraumaPromptId}'s identical
 * precedent) so a caller — a prompt-answering policy (`unit-ai` Phase 3.2), a test — can
 * recognize/build the shape without duplicating the literal.
 */
export function damageTypePromptId(weaponId: string): string {
  return `damage_type:${weaponId}`;
}

/** Is `key` a recorded/broadcast answer to a {@link damageTypePromptId} prompt? A same-actor prompt (the attacker answers their own weapon's choice), so — unlike {@link import("../reactionRegistry").isReactionPromptId} — this needs no barrage de-namespacing or cross-actor routing; it exists purely so a caller recognizes the shape by symbol instead of a hand-copied string prefix. */
export function isVariableDamagePromptId(key: string): boolean {
  return key.startsWith("damage_type:");
}

/** The fixed option set a {@link damageTypePromptId} prompt offers — RAW's three damage types a `variable` weapon can fire as. */
export const VARIABLE_DAMAGE_TYPE_OPTIONS = ["kinetic", "energy", "explosive"] as const;

/**
 * Resolve a {@link RequestAttack} into a recorded resolution (Decision 2,
 * draw-and-record). Runs the rules **once**, sourcing every die through
 * `input.dice` and recording each as a step:
 *
 *   1. **`to_hit`** — roll `d20 ± accuracy`, add the attacker's frozen `attackBonus`,
 *      compare the total to the target's frozen **Evasion**. Record d20, accuracy
 *      contribution, bonus, total, defense, and `result` (miss/hit/crit).
 *   2. On a **hit/crit**, **`damage`** — a trivial flat roll (crit double-rolls,
 *      keeps the higher half — the Phase-0 dice module's crit mode), recorded.
 *   3. On a **hit/crit**, **`hp_delta`** — the one grounded mutation: drop the
 *      target's HP by the damage total. (A miss produces only the `to_hit` record —
 *      no damage, no HP change.)
 *
 * The attacker's `attackBonus` defaults to `0` when the frozen base predates A1
 * (the field is optional — see `CombatantBase`). Returns `kind: "resolved"` always
 * in A1; the `needs_decision` branch is reserved (architecture decision (a)).
 *
 * `actorId` is who the resolution is attributed to (the client that sent the intent,
 * off the wire envelope — `RequestAttack` itself carries no actor). It is stamped on
 * the resulting `apply_resolved` command so the room keys this actor's undo stack and
 * the retained log records who acted. Defaults to `"authority"`.
 *
 * @throws {ResolveError} if the attacker or target id is unknown in `state`.
 */
export type ResolveArgs = {
  state: EncounterState;
  intent: RequestAttack;
  input: InputSource;
  actorId?: string;
  effects?: EffectsConfig;
};

/**
 * {@link ResolveArgs} plus the resolution-scoped fact ledger (`LL0 DSL Primitives V`, P1) —
 * every INTERNAL dispatch past {@link resolve}'s own top (`fireAttackDeclared`,
 * `resolveSingleTarget`, `resolveAreaAttack`) carries this widened shape so the SAME ledger
 * object threads by reference through the whole call, never the bare public {@link
 * ResolveArgs} an external caller supplies (the ledger is purely resolution-internal
 * plumbing, not something a caller constructs or reads).
 */
export type ResolveArgsWithLedger = ResolveArgs & {
  ledger: ResolutionLedger;
};
